import ReactDOM from 'react-dom/client'
import type { Root } from 'react-dom/client'
// The shared Outlook styles, inlined (?inline → CSS string) so we can mount them
// into the widget's shadow DOM alongside WIDGET_CSS. Single source with the
// dashboard, which imports the same file normally.
import outlookCss from '@netwatchai/chat-core/outlook.css?inline'
import emptyStateCss from '@netwatchai/chat-core/empty-state.css?inline'
import faqCss from '@netwatchai/chat-core/faq.css?inline'
import salutationCss from '@netwatchai/chat-core/salutation.css?inline'
import findingsCss from '@netwatchai/chat-core/findings.css?inline'
import composerCss from '@netwatchai/chat-core/composer.css?inline'
import { ChatWidget } from './ChatWidget'

// Widget-side overrides mounted AFTER the shared chat-core sheets so they win
// ties. The outlook dialog scrim is full-viewport on a real page; inside the
// embedded widget it must stay contained to the panel — dimming the customer's
// host page is not this widget's call to make.
const CHAT_CORE_OVERRIDES = `
  .nw-ol-dim { position: absolute; inset: 0; z-index: 5; padding: 14px; border-radius: 16px; }
`

const WIDGET_CSS = `
  :host {
    all: initial;
    --nw-primary: #6366f1;
    --nw-primary-dark: #4f46e5;
    --nw-primary-darker: #4338ca;
    --nw-accent: #8b5cf6;
    --nw-bg: #ffffff;
    --nw-bg-subtle: #f9fafb;
    --nw-bg-message: #f3f4f6;
    --nw-border: #e5e7eb;
    --nw-text: #111827;
    --nw-text-secondary: #6b7280;
    --nw-text-tertiary: #9ca3af;
    /* Categorical chart series ramp (light steps — the widget's default theme).
       Validated with the dataviz six checks against both surfaces; see
       docs/charts.md. Fixed order, never cycled; the two hues nearest semaphore
       green/orange sit last so a 2-4 series chart never shows one. */
    --nw-chart-1: #2a78d6;
    --nw-chart-2: #eda100;
    --nw-chart-3: #e87ba4;
    --nw-chart-4: #4a3aa7;
    --nw-chart-5: #eb6834;
    --nw-chart-6: #1baf7a;
    --nw-shadow-lg: 0 25px 50px -12px rgba(0, 0, 0, 0.18), 0 10px 20px -5px rgba(0, 0, 0, 0.08);
    --nw-shadow-md: 0 10px 25px -5px rgba(0, 0, 0, 0.12), 0 4px 10px -3px rgba(0, 0, 0, 0.06);
    --nw-radius: 16px;
    --nw-radius-sm: 10px;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
    font-size: 14px;
    line-height: 1.5;
    color: var(--nw-text);
  }

  *, *::before, *::after {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  .nw-root {
    position: fixed;
    bottom: 24px;
    right: 24px;
    z-index: 2147483647;
    color: var(--nw-text);
  }

  /* ---- Placement (dock left / right corner) ----------------------- */
  /* The left corner just shifts the root; panel + resize-grip positions are
     set inline (they depend on where the launcher actually sits, which can be
     a free-dragged spot, not only a corner). */
  .nw-root.nw-pos-left { right: auto; left: 24px; }
  /* When the grip would land on the top-right header controls, mirror the
     header so the buttons sit against the opposite edge instead. */
  .nw-root.nw-mirror .nw-header { flex-direction: row-reverse; }
  .nw-root.nw-mirror .nw-header-text { text-align: right; }

  /* ---- Dark theme (CSS variable overrides) ------------------------ */
  .nw-root.nw-theme-dark {
    --nw-bg: #1f2430;
    --nw-bg-subtle: #181c26;
    --nw-bg-message: #252b38;
    --nw-border: #333a48;
    --nw-text: #e7eaf0;
    --nw-text-secondary: #a3acbd;
    --nw-text-tertiary: #6b7484;
    /* Dark steps of the same ramp — re-stepped for the dark surface, not flipped. */
    --nw-chart-1: #3987e5;
    --nw-chart-2: #c98500;
    --nw-chart-3: #d55181;
    --nw-chart-4: #9085e9;
    --nw-chart-5: #d95926;
    --nw-chart-6: #199e70;
    --nw-shadow-lg: 0 25px 50px -12px rgba(0, 0, 0, 0.6), 0 10px 20px -5px rgba(0, 0, 0, 0.4);
    --nw-shadow-md: 0 10px 25px -5px rgba(0, 0, 0, 0.5), 0 4px 10px -3px rgba(0, 0, 0, 0.4);
  }
  .nw-root.nw-theme-dark .nw-bubble code { background: rgba(255, 255, 255, 0.12); }
  .nw-root.nw-theme-dark .nw-msg.user .nw-bubble code { background: rgba(255, 255, 255, 0.22); }
  .nw-root.nw-theme-dark .nw-bubble pre { background: rgba(255, 255, 255, 0.06); }
  .nw-root.nw-theme-dark .nw-messages::-webkit-scrollbar-thumb,
  .nw-root.nw-theme-dark .nw-history-body::-webkit-scrollbar-thumb,
  .nw-root.nw-theme-dark .nw-think-body::-webkit-scrollbar-thumb { background: rgba(255, 255, 255, 0.18); }
  .nw-root.nw-theme-dark .nw-feedback-notes { background: var(--nw-bg-subtle); color: var(--nw-text); }
  .nw-root.nw-theme-dark .nw-tab.is-active,
  .nw-root.nw-theme-dark .nw-segment-btn.is-active { color: #a5b4fc; }

  /* Toggle button */
  .nw-toggle {
    width: 60px;
    height: 60px;
    border-radius: 50%;
    border: none;
    cursor: pointer;
    background: linear-gradient(135deg, var(--nw-primary) 0%, var(--nw-accent) 100%);
    box-shadow: var(--nw-shadow-md), 0 0 0 0 rgba(99, 102, 241, 0.4);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
    position: relative;
  }
  .nw-toggle:hover {
    transform: translateY(-2px) scale(1.04);
    box-shadow: var(--nw-shadow-lg), 0 0 0 8px rgba(99, 102, 241, 0.12);
  }
  .nw-toggle:active {
    transform: translateY(0) scale(0.98);
  }
  .nw-toggle svg {
    width: 26px;
    height: 26px;
    transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  }
  .nw-toggle.is-open svg {
    transform: rotate(180deg);
  }

  /* Pulse ring */
  .nw-toggle::after {
    content: "";
    position: absolute;
    inset: -4px;
    border-radius: 50%;
    border: 2px solid rgba(99, 102, 241, 0.3);
    opacity: 0;
    animation: nw-pulse 2.2s ease-out infinite;
    pointer-events: none;
  }
  @keyframes nw-pulse {
    0% { transform: scale(0.9); opacity: 0.8; }
    100% { transform: scale(1.4); opacity: 0; }
  }

  /* Chat panel */
  .nw-panel {
    position: absolute;
    isolation: isolate;  /* own stacking context for the panel's layers */
    bottom: 76px;
    right: 0;
    background: var(--nw-bg);
    border-radius: var(--nw-radius);
    box-shadow: var(--nw-shadow-lg);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    transform-origin: bottom right;
    animation: nw-panel-in 0.22s cubic-bezier(0.16, 1, 0.3, 1);
  }
  .nw-panel.resizing {
    animation: none;
    user-select: none;
  }

  /* Resize handle (top-left corner since panel is anchored bottom-right) */
  .nw-resize {
    position: absolute;
    top: 0;
    left: 0;
    width: 18px;
    height: 18px;
    cursor: nwse-resize;
    z-index: 10;
    display: flex;
    align-items: center;
    justify-content: center;
    color: rgba(255, 255, 255, 0.7);
    transition: color 0.15s ease;
  }
  .nw-resize:hover { color: rgba(255, 255, 255, 1); }
  .nw-resize svg { width: 12px; height: 12px; }
  @keyframes nw-panel-in {
    from { opacity: 0; transform: translateY(12px) scale(0.96); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }

  /* Header */
  .nw-header {
    padding: 18px 20px;
    background: linear-gradient(135deg, var(--nw-primary) 0%, var(--nw-accent) 100%);
    color: #fff;
    display: flex;
    align-items: center;
    gap: 12px;
    flex-shrink: 0;
  }
  .nw-avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.2);
    backdrop-filter: blur(8px);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    position: relative;
  }
  .nw-avatar svg { width: 22px; height: 22px; color: #fff; }
  .nw-avatar::after {
    content: "";
    position: absolute;
    bottom: 0;
    right: 0;
    width: 11px;
    height: 11px;
    background: #34d399;
    border: 2px solid #fff;
    border-radius: 50%;
  }
  .nw-header-text { flex: 1; min-width: 0; }
  .nw-header-title {
    font-size: 15px;
    font-weight: 600;
    line-height: 1.2;
    letter-spacing: -0.01em;
  }
  .nw-header-subtitle {
    font-size: 12px;
    opacity: 0.85;
    margin-top: 2px;
  }
  .nw-close {
    background: rgba(255, 255, 255, 0.15);
    border: none;
    cursor: pointer;
    color: #fff;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.15s ease;
  }
  .nw-close:hover { background: rgba(255, 255, 255, 0.28); }
  .nw-close svg { width: 16px; height: 16px; }

  /* History drawer (overlays the messages region) */
  .nw-history {
    position: absolute;
    top: 76px;
    left: 0;
    right: 0;
    bottom: 0;
    background: var(--nw-bg);
    display: flex;
    flex-direction: column;
    z-index: 5;
    animation: nw-history-in 0.18s cubic-bezier(0.16, 1, 0.3, 1);
  }
  @keyframes nw-history-in {
    from { opacity: 0; transform: translateY(-4px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .nw-history-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px;
    border-bottom: 1px solid var(--nw-border);
    background: var(--nw-bg-subtle);
    flex-shrink: 0;
  }
  .nw-history-title {
    font-size: 13px;
    font-weight: 600;
    color: var(--nw-text);
    letter-spacing: -0.005em;
  }
  .nw-history-new {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background: var(--nw-bg);
    border: 1px solid var(--nw-border);
    border-radius: 8px;
    padding: 5px 10px;
    font-family: inherit;
    font-size: 12.5px;
    color: var(--nw-text);
    cursor: pointer;
    transition: all 0.15s ease;
  }
  .nw-history-new:hover {
    border-color: var(--nw-primary);
    color: var(--nw-primary);
  }
  .nw-history-new svg { width: 12px; height: 12px; }
  .nw-history-body {
    flex: 1;
    overflow-y: auto;
    padding: 8px;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .nw-history-body::-webkit-scrollbar { width: 6px; }
  .nw-history-body::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.12);
    border-radius: 3px;
  }
  .nw-history-empty {
    text-align: center;
    padding: 32px 16px;
    color: var(--nw-text-secondary);
    font-size: 13px;
  }
  .nw-history-item {
    text-align: left;
    background: transparent;
    border: 1px solid transparent;
    border-radius: 10px;
    padding: 10px 12px;
    font-family: inherit;
    cursor: pointer;
    transition: all 0.12s ease;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  .nw-history-item:hover:not(:disabled) {
    background: var(--nw-bg-subtle);
    border-color: var(--nw-border);
  }
  .nw-history-item:disabled { opacity: 0.6; cursor: wait; }
  .nw-history-item-title {
    font-size: 13.5px;
    color: var(--nw-text);
    font-weight: 500;
    line-height: 1.35;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }
  .nw-history-item-meta {
    font-size: 11.5px;
    color: var(--nw-text-tertiary);
  }

  /* Tab bar (Chat | Outlook | Settings + Open in app) */
  .nw-tabs {
    display: flex;
    background: var(--nw-bg);
    border-bottom: 1px solid var(--nw-border);
    flex-shrink: 0;
  }
  .nw-tab {
    flex: 1;
    background: transparent;
    border: none;
    border-bottom: 2px solid transparent;
    padding: 10px 12px;
    font-family: inherit;
    font-size: 13px;
    font-weight: 500;
    color: var(--nw-text-secondary);
    cursor: pointer;
    transition: color 0.12s ease, border-color 0.12s ease, background 0.12s ease;
    position: relative;
  }
  .nw-tab:hover {
    color: var(--nw-text);
    background: var(--nw-bg-subtle);
  }
  .nw-tab.is-active {
    color: var(--nw-primary-dark);
    border-bottom-color: var(--nw-primary);
  }
  /* "Open in app" — a launcher pinned to the right of the tab bar, not a tab. */
  .nw-open-app {
    flex: 0 0 auto;
    margin-left: auto;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    background: transparent;
    border: none;
    border-left: 1px solid var(--nw-border);
    padding: 10px 12px;
    font-family: inherit;
    font-size: 12.5px;
    font-weight: 500;
    color: var(--nw-text-secondary);
    cursor: pointer;
    transition: color 0.12s ease, background 0.12s ease;
  }
  .nw-open-app:hover {
    color: var(--nw-primary-dark);
    background: var(--nw-bg-subtle);
  }
  .nw-open-app svg { width: 13px; height: 13px; }
  .nw-open-app-label { white-space: nowrap; }

  /* In-panel confirm dialog ("Continue in app?"). Scrim contained to the
     panel — never dims the customer's host page. */
  .nw-confirm-dim {
    position: absolute;
    inset: 0;
    z-index: 20;
    background: rgba(5, 9, 15, 0.45);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    border-radius: var(--nw-radius);
  }
  .nw-confirm {
    background: var(--nw-bg);
    border: 1px solid var(--nw-border);
    border-radius: var(--nw-radius-sm);
    box-shadow: var(--nw-shadow-md);
    padding: 16px;
    width: 100%;
    max-width: 280px;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .nw-confirm-title { font-size: 14px; font-weight: 600; color: var(--nw-text); }
  .nw-confirm-msg { font-size: 13px; color: var(--nw-text-secondary); line-height: 1.5; }
  .nw-confirm-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 6px;
  }
  .nw-confirm-btn {
    background: var(--nw-bg);
    border: 1px solid var(--nw-border);
    border-radius: 8px;
    padding: 7px 14px;
    font-family: inherit;
    font-size: 13px;
    font-weight: 500;
    color: var(--nw-text);
    cursor: pointer;
    text-decoration: none;
    transition: color 0.12s ease, border-color 0.12s ease, background 0.12s ease;
  }
  .nw-confirm-btn:hover {
    border-color: var(--nw-primary);
    color: var(--nw-primary-dark);
  }
  .nw-confirm-btn.is-primary {
    background: var(--nw-primary);
    border-color: var(--nw-primary);
    color: #fff;
  }
  .nw-confirm-btn.is-primary:hover {
    background: var(--nw-primary-dark);
    border-color: var(--nw-primary-dark);
    color: #fff;
  }

  /* Incidents panel */
  .nw-incidents {
    flex: 1;
    display: flex;
    flex-direction: column;
    background: var(--nw-bg);
    overflow: hidden;
    min-height: 0;
  }
  .nw-incidents-header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 14px;
    border-bottom: 1px solid var(--nw-border);
    background: var(--nw-bg-subtle);
    flex-shrink: 0;
  }
  .nw-incidents-title {
    font-size: 13px;
    font-weight: 600;
    color: var(--nw-text);
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .nw-incidents-count {
    color: var(--nw-text-tertiary);
    font-weight: 500;
  }
  .nw-incidents-refresh {
    width: 28px;
    height: 28px;
    border-radius: 6px;
    border: 1px solid var(--nw-border);
    background: var(--nw-bg);
    color: var(--nw-text-secondary);
    font-size: 14px;
    cursor: pointer;
    font-family: inherit;
  }
  .nw-incidents-refresh:hover:not(:disabled) {
    border-color: var(--nw-primary);
    color: var(--nw-primary);
  }
  .nw-incidents-refresh:disabled { opacity: 0.5; cursor: wait; }
  .nw-incidents-back {
    width: 28px;
    height: 28px;
    border-radius: 6px;
    border: 1px solid var(--nw-border);
    background: var(--nw-bg);
    color: var(--nw-text-secondary);
    font-size: 18px;
    line-height: 1;
    cursor: pointer;
    font-family: inherit;
  }
  .nw-incidents-back:hover {
    border-color: var(--nw-primary);
    color: var(--nw-primary);
  }
  .nw-incidents-body {
    flex: 1;
    overflow-y: auto;
    padding: 6px;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .nw-incidents-empty {
    text-align: center;
    padding: 32px 14px;
    color: var(--nw-text-secondary);
    font-size: 13px;
  }

  /* Date-grouped collapsible sections */
  .nw-incidents-group {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .nw-incidents-group-head {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 10px;
    background: transparent;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-family: inherit;
    font-size: 12.5px;
    color: var(--nw-text-secondary, #6b7280);
    text-align: left;
    transition: background 0.1s;
    margin-top: 6px;
  }
  .nw-incidents-group-head:hover {
    background: var(--nw-bg-subtle, rgba(0,0,0,0.04));
    color: var(--nw-text, #1f2937);
  }
  .nw-incidents-group-chevron {
    display: inline-block;
    transition: transform 0.15s ease;
    transform: rotate(0deg);
    font-size: 10px;
    opacity: 0.7;
  }
  .nw-incidents-group-chevron.is-open {
    transform: rotate(90deg);
  }
  .nw-incidents-group-label {
    font-weight: 600;
    letter-spacing: 0.01em;
  }
  .nw-incidents-group-count {
    margin-left: auto;
    background: var(--nw-bg-subtle, #eef0f3);
    color: var(--nw-text-secondary, #6b7280);
    border-radius: 999px;
    padding: 1px 8px;
    font-size: 11px;
    font-weight: 600;
  }

  /* Importance pill (sortable signal on each row) */
  .nw-importance-pill {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 22px;
    height: 22px;
    border-radius: 6px;
    color: #fff;
    font-size: 12px;
    font-weight: 700;
    padding: 0 6px;
    box-shadow: 0 1px 1px rgba(0,0,0,0.08);
  }
  .nw-importance-pill.is-user {
    box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.45);  /* user-set ring */
  }

  /* Incident list row */
  .nw-incident-row {
    text-align: left;
    background: transparent;
    border: 1px solid transparent;
    border-radius: 10px;
    padding: 10px 12px;
    font-family: inherit;
    cursor: pointer;
    transition: background 0.12s ease, border-color 0.12s ease;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .nw-incident-row:hover {
    background: var(--nw-bg-subtle);
    border-color: var(--nw-border);
  }
  .nw-incident-row-top {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  /* Severity icons — shape + fill carry the signal, not colour alone. */
  .nw-sev-icon {
    flex-shrink: 0;
    vertical-align: -2px;
    display: inline-block;
  }
  .nw-sev-badge {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    white-space: nowrap;
  }
  .nw-sev-label { font-weight: 600; }
  .nw-incident-host {
    font-size: 13px;
    font-weight: 600;
    color: var(--nw-text);
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .nw-status {
    font-size: 10.5px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    padding: 2px 6px;
    border-radius: 4px;
    font-weight: 600;
  }
  .nw-status-pending       { background: #f3f4f6; color: #6b7280; }
  .nw-status-investigating { background: #dbeafe; color: #1d4ed8; }
  .nw-status-investigated  { background: #dcfce7; color: #15803d; }
  .nw-status-failed        { background: #fee2e2; color: #b91c1c; }
  .nw-incident-name {
    font-size: 13px;
    color: var(--nw-text);
    line-height: 1.4;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }
  .nw-incident-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    font-size: 11.5px;
    color: var(--nw-text-tertiary);
  }
  .nw-strategy {
    background: rgba(99, 102, 241, 0.08);
    color: var(--nw-primary-dark);
    border-radius: 4px;
    padding: 1px 6px;
    font-weight: 500;
  }

  /* Incident detail */
  .nw-detail-title {
    font-size: 13px;
  }
  .nw-detail-body {
    flex: 1;
    overflow-y: auto;
    padding: 14px;
    display: flex;
    flex-direction: column;
    gap: 14px;
  }
  .nw-detail-summary {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .nw-detail-spacer { flex: 1; }
  .nw-detail-meta {
    font-size: 12px;
    color: var(--nw-text-secondary);
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  .nw-detail-meta b {
    color: var(--nw-text);
    font-weight: 600;
  }
  .nw-detail-meta-hint {
    color: var(--nw-text-tertiary);
    font-size: 11px;
  }

  /* Investigation log panel — drops below the header when "Logs" toggled */
  .nw-log-panel {
    background: #111827;
    color: #e5e7eb;
    font-family: ui-monospace, "SF Mono", Menlo, monospace;
    font-size: 11.5px;
    line-height: 1.45;
    padding: 8px 10px;
    max-height: 280px;
    overflow-y: auto;
    border-bottom: 1px solid var(--nw-border);
    flex-shrink: 0;
  }
  .nw-log-empty {
    text-align: center;
    color: #9ca3af;
    padding: 18px 10px;
  }
  .nw-log-truncated {
    color: #9ca3af;
    font-style: italic;
    margin-bottom: 4px;
  }
  .nw-log-line {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    padding: 2px 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  }
  .nw-log-line:last-child { border-bottom: none; }
  .nw-log-ts {
    color: #9ca3af;
    white-space: nowrap;
  }
  .nw-log-kind {
    color: #93c5fd;
    font-weight: 600;
    min-width: 90px;
  }
  .nw-log-name { color: #e5e7eb; }
  .nw-log-phase { color: #9ca3af; }
  .nw-log-data {
    color: #d1d5db;
    flex: 1;
    min-width: 0;
    overflow-wrap: anywhere;
    word-break: break-all;
  }
  .nw-log-tool       .nw-log-kind { color: #fbbf24; }
  .nw-log-tool_result .nw-log-kind { color: #34d399; }
  .nw-log-route      .nw-log-kind { color: #a78bfa; }
  .nw-log-thought    .nw-log-kind { color: #f9a8d4; }
  .nw-log-status {
    color: #9ca3af;
    font-style: italic;
    padding: 2px 0 6px;
    border-bottom: 1px dashed rgba(255, 255, 255, 0.1);
    margin-bottom: 4px;
  }
  .nw-log-live {
    color: #34d399;
    font-weight: 600;
    font-style: normal;
  }
  .nw-detail-section {
    border: 1px solid var(--nw-border);
    border-radius: 10px;
    padding: 10px 12px;
    background: var(--nw-bg);
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  .nw-detail-section-head {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    font-weight: 600;
    color: var(--nw-text);
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }
  .nw-detail-section-head.nw-warn { color: #b45309; }
  .nw-detail-statement {
    font-size: 13px;
    line-height: 1.5;
    color: var(--nw-text);
  }
  .nw-detail-list {
    margin: 0;
    padding-left: 20px;
    font-size: 12.5px;
    line-height: 1.5;
    color: var(--nw-text);
  }
  .nw-detail-list li { margin: 2px 0; }
  .nw-detail-claims {
    margin: 0;
    padding-left: 16px;
    font-size: 12.5px;
    line-height: 1.45;
    list-style: none;
  }
  .nw-detail-claims li {
    margin: 4px 0;
    padding-left: 0;
  }
  .nw-claim {
    color: var(--nw-text);
  }
  .nw-claim-evidence {
    color: var(--nw-text-secondary);
    font-style: italic;
    padding-left: 14px;
    font-size: 11.5px;
  }
  .nw-validity {
    font-size: 11px;
    font-weight: 600;
    padding: 2px 6px;
    border-radius: 4px;
    text-transform: none;
    letter-spacing: 0;
  }
  .nw-validity-high { background: #dcfce7; color: #15803d; }
  .nw-validity-med  { background: #fef3c7; color: #b45309; }
  .nw-validity-low  { background: #fee2e2; color: #b91c1c; }
  .nw-category {
    font-size: 11px;
    color: var(--nw-text-tertiary);
    background: var(--nw-bg-subtle);
    padding: 1px 6px;
    border-radius: 4px;
    text-transform: none;
    letter-spacing: 0;
    font-family: ui-monospace, "SF Mono", Menlo, monospace;
  }
  .nw-corr-block {
    font-size: 12.5px;
    color: var(--nw-text);
    line-height: 1.5;
  }
  .nw-corr-block b { font-weight: 600; }

  /* --- Feedback section -------------------------------------------- */
  .nw-feedback {
    background: var(--nw-bg-subtle, #f7f8fa);
    border: 1px solid var(--nw-border, #e3e7ec);
    border-radius: 10px;
    padding: 14px 14px 12px;
  }
  .nw-feedback-saved-at {
    margin-left: 8px;
    font-weight: 400;
    font-size: 11.5px;
    color: var(--nw-text-muted, #6b7280);
  }
  .nw-feedback-row {
    margin: 10px 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  .nw-feedback-label {
    font-size: 12.5px;
    font-weight: 600;
    color: var(--nw-text, #1f2937);
  }
  .nw-feedback-optional {
    font-weight: 400;
    color: var(--nw-text-muted, #6b7280);
    font-size: 11.5px;
  }
  .nw-feedback-slider-wrap {
    display: flex;
    align-items: center;
    gap: 12px;
  }
  .nw-feedback-slider {
    flex: 1;
    -webkit-appearance: none;
    appearance: none;
    height: 6px;
    border-radius: 999px;
    background: linear-gradient(to right,
      hsl(120, 70%, 45%) 0%,
      hsl(60, 75%, 50%) 50%,
      hsl(0, 75%, 50%) 100%);
    outline: none;
    cursor: pointer;
  }
  .nw-feedback-slider::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 18px; height: 18px;
    border-radius: 50%;
    background: #fff;
    border: 2px solid hsl(var(--nw-slider-hue, 60), 70%, 45%);
    cursor: pointer;
    box-shadow: 0 1px 3px rgba(0,0,0,0.15);
  }
  .nw-feedback-slider::-moz-range-thumb {
    width: 18px; height: 18px;
    border-radius: 50%;
    background: #fff;
    border: 2px solid hsl(var(--nw-slider-hue, 60), 70%, 45%);
    cursor: pointer;
  }
  .nw-feedback-slider-value {
    min-width: 28px;
    height: 28px;
    border-radius: 6px;
    color: #fff;
    font-weight: 700;
    font-size: 14px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0 8px;
  }
  .nw-feedback-slider-legend {
    display: flex;
    justify-content: space-between;
    font-size: 11px;
    color: var(--nw-text-muted, #6b7280);
  }
  .nw-feedback-stars {
    display: flex;
    gap: 4px;
  }
  .nw-feedback-star {
    background: none;
    border: none;
    padding: 2px 4px;
    cursor: pointer;
    font-size: 22px;
    color: #d1d5db;
    line-height: 1;
    transition: color 0.1s;
  }
  .nw-feedback-star:hover,
  .nw-feedback-star.on {
    color: #f59e0b;
  }
  .nw-feedback-notes {
    width: 100%;
    box-sizing: border-box;
    border: 1px solid var(--nw-border, #d1d5db);
    border-radius: 8px;
    padding: 8px 10px;
    font-family: inherit;
    font-size: 13px;
    color: var(--nw-text, #1f2937);
    background: #fff;
    resize: vertical;
    min-height: 60px;
  }
  .nw-feedback-notes:focus {
    outline: none;
    border-color: var(--nw-accent, #6366f1);
    box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.15);
  }
  .nw-feedback-actions {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: 8px;
  }
  .nw-feedback-save {
    background: var(--nw-accent, #6366f1);
    color: #fff;
    border: none;
    border-radius: 8px;
    padding: 7px 16px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.1s;
  }
  .nw-feedback-save:hover:not(:disabled) {
    background: var(--nw-accent-hover, #4f46e5);
  }
  .nw-feedback-save:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  .nw-feedback-flash {
    color: #059669;
    font-size: 12.5px;
    font-weight: 500;
  }
  .nw-feedback-error {
    color: #b91c1c;
    font-size: 12.5px;
  }

  /* Messages */
  .nw-messages {
    flex: 1;
    overflow-y: auto;
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    background: var(--nw-bg-subtle);
    scroll-behavior: smooth;
  }
  .nw-messages::-webkit-scrollbar { width: 6px; }
  .nw-messages::-webkit-scrollbar-track { background: transparent; }
  .nw-messages::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.12);
    border-radius: 3px;
  }
  .nw-messages::-webkit-scrollbar-thumb:hover { background: rgba(0, 0, 0, 0.2); }

  /* Empty state — the chat landing is the greeting and nothing else. It is a
     single short line, so unlike the card stack it used to hold it centres in
     the pane rather than top-aligning and stretching. */
  .nw-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex: 1;
    min-height: 0;
    gap: 10px;
    color: var(--nw-text-secondary);
    /* The shared sheet scales the heading with the viewport, which is wrong for
       a ~380px panel floating over someone else's page: it would track the
       window, not the widget. Pin it instead. */
    --nw-hello-size: 22px;
    --nw-hello-gap: 10px;
  }

  /* Messages */
  .nw-msg { display: flex; gap: 8px; max-width: 90%; }
  .nw-msg.user { align-self: flex-end; flex-direction: row-reverse; }
  .nw-msg.assistant { align-self: flex-start; }

  .nw-bubble {
    padding: 10px 14px;
    border-radius: 16px;
    font-size: 14px;
    line-height: 1.5;
    word-wrap: break-word;
    overflow-wrap: anywhere;
  }
  .nw-msg.user .nw-bubble {
    background: linear-gradient(135deg, var(--nw-primary) 0%, var(--nw-primary-dark) 100%);
    color: #fff;
    border-bottom-right-radius: 4px;
    white-space: pre-wrap;
  }
  .nw-msg.assistant .nw-bubble {
    background: var(--nw-bg);
    color: var(--nw-text);
    border: 1px solid var(--nw-border);
    border-bottom-left-radius: 4px;
  }
  .nw-bubble p { margin: 0; }
  .nw-bubble p + p { margin-top: 8px; }
  .nw-bubble code {
    background: rgba(0, 0, 0, 0.06);
    padding: 1px 5px;
    border-radius: 4px;
    font-family: ui-monospace, "SF Mono", Menlo, monospace;
    font-size: 0.9em;
  }
  .nw-msg.user .nw-bubble code {
    background: rgba(255, 255, 255, 0.2);
  }
  .nw-bubble pre {
    background: rgba(0, 0, 0, 0.05);
    padding: 10px;
    border-radius: 8px;
    overflow-x: auto;
    margin: 6px 0;
    font-size: 12.5px;
  }
  .nw-bubble pre code { background: transparent; padding: 0; }
  .nw-bubble ul, .nw-bubble ol { padding-left: 20px; margin: 4px 0; }
  .nw-bubble li { margin: 2px 0; }
  .nw-bubble a {
    color: inherit;
    text-decoration: underline;
    text-underline-offset: 2px;
  }

  /* Tables */
  .nw-bubble table {
    width: 100%;
    border-collapse: collapse;
    margin: 8px 0;
    font-size: 12.5px;
    background: var(--nw-bg);
    border-radius: 8px;
    overflow: hidden;
    display: block;
    overflow-x: auto;
    white-space: nowrap;
    border: 1px solid var(--nw-border);
  }
  .nw-bubble thead {
    background: var(--nw-bg-subtle);
  }
  .nw-bubble th {
    text-align: left;
    padding: 8px 12px;
    font-weight: 600;
    color: var(--nw-text);
    border-bottom: 1px solid var(--nw-border);
    white-space: nowrap;
  }
  .nw-bubble td {
    padding: 7px 12px;
    color: var(--nw-text);
    border-bottom: 1px solid var(--nw-border);
  }
  .nw-bubble tbody tr:last-child td { border-bottom: none; }
  .nw-bubble tbody tr:hover { background: var(--nw-bg-subtle); }
  .nw-msg.user .nw-bubble table {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.25);
  }
  .nw-msg.user .nw-bubble thead { background: rgba(255, 255, 255, 0.12); }
  .nw-msg.user .nw-bubble th,
  .nw-msg.user .nw-bubble td {
    color: #fff;
    border-color: rgba(255, 255, 255, 0.18);
  }
  .nw-msg.user .nw-bubble tbody tr:hover { background: rgba(255, 255, 255, 0.08); }

  /* Structured netwatch-table blocks (severity-aware) */
  .nw-ntable-wrap {
    margin: 8px 0;
    border: 1px solid var(--nw-border);
    border-radius: 10px;
    overflow: hidden;
  }
  .nw-ntable-title {
    padding: 7px 12px;
    font-size: 12px;
    font-weight: 600;
    color: var(--nw-text-secondary);
    background: var(--nw-bg-subtle);
    border-bottom: 1px solid var(--nw-border);
  }
  .nw-ntable {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
  }
  .nw-ntable thead { background: var(--nw-bg-subtle); }
  .nw-ntable th {
    text-align: left;
    padding: 8px 12px;
    font-weight: 700;
    font-size: 13px;
    color: var(--nw-text);
    white-space: nowrap;
  }
  .nw-ntable td {
    padding: 8px 12px;
    color: var(--nw-text);
    border-top: 1px solid var(--nw-border);
    vertical-align: middle;
  }
  .nw-ntable-cell-sev { white-space: nowrap; }
  /* Auto row-number column (every table is numbered). */
  .nw-ntable-num {
    width: 1%;
    white-space: nowrap;
    text-align: right;
    color: var(--nw-text-tertiary);
    font-variant-numeric: tabular-nums;
  }
  .nw-ntable tbody tr:hover { background: var(--nw-bg-subtle); }
  /* Grouped tables: the group-leading cells (account/#/status) span their
     sub-rows; give them a subtle header treatment and start each group with a
     stronger top border so groups read as distinct blocks. */
  .nw-ntable-group-cell {
    vertical-align: top;
    font-weight: 600;
    background: var(--nw-bg-subtle);
  }
  .nw-ntable tbody tr.nw-ntable-group-start td { border-top: 2px solid var(--nw-border); }
  .nw-ntable tbody tr.nw-ntable-group-start:first-child td { border-top: none; }
  .nw-ntable-pending {
    margin: 8px 0;
    padding: 10px 12px;
    font-size: 12.5px;
    color: var(--nw-text-tertiary);
    border: 1px dashed var(--nw-border);
    border-radius: 10px;
  }
  .nw-ntable-raw {
    background: rgba(0, 0, 0, 0.05);
    padding: 10px;
    border-radius: 8px;
    overflow-x: auto;
    font-size: 12px;
  }

  /* netwatch charts — mirrors chat-content.css; the plot is the shared NwChart
     (Chart.js canvas), which reads the tokens below out of this cascade. */
  .nw-nchart-wrap {
    margin: 8px 0;
    padding-bottom: 6px;
    border: 1px solid var(--nw-border);
    border-radius: 10px;
    overflow: hidden;
    container-type: inline-size;
  }
  .nw-nchart-title {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 7px 8px 7px 12px;
    margin-bottom: 6px;
    font-size: 12px;
    font-weight: 600;
    color: var(--nw-text-secondary);
    background: var(--nw-bg-subtle);
    border-bottom: 1px solid var(--nw-border);
  }
  .nw-nchart-title-text { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .nw-nchart-actions { flex: none; display: flex; align-items: center; gap: 2px; }
  .nw-nchart-act {
    display: inline-flex; align-items: center; gap: 5px; height: 24px; padding: 0 6px;
    border: 1px solid transparent; border-radius: 7px; background: none;
    color: var(--nw-text-tertiary); font: inherit; font-size: 11px; font-weight: 600;
    cursor: pointer; opacity: 0.65;
    transition: opacity .12s ease, color .12s ease, border-color .12s ease;
  }
  .nw-nchart-wrap:hover .nw-nchart-act,
  .nw-nchart-dialog .nw-nchart-act { opacity: 1; }
  .nw-nchart-act:hover:not(:disabled) { color: var(--nw-text); border-color: var(--nw-border); }
  .nw-nchart-act:disabled { cursor: default; opacity: .3; }
  .nw-nchart-act:focus-visible { opacity: 1; outline: 2px solid var(--nw-accent); outline-offset: 1px; }

  .nw-chart-zoom-reset {
    position: absolute; top: 4px; right: 6px; padding: 3px 8px;
    font-size: 11px; font-weight: 600; color: var(--nw-text-secondary);
    background: var(--nw-bg); border: 1px solid var(--nw-border);
    border-radius: 7px; cursor: pointer;
  }
  .nw-chart-zoom-reset:hover { color: var(--nw-text); }

  /* expanded chart dialog — same chart, the whole viewport */
  .nw-nchart-dim {
    position: fixed; inset: 0; z-index: 2147483647;
    display: flex; align-items: center; justify-content: center; padding: 24px;
    background: rgba(5, 9, 15, .52);
    backdrop-filter: blur(7px); -webkit-backdrop-filter: blur(7px);
  }
  .nw-nchart-dialog {
    width: min(1000px, 100%); max-height: 100%; overflow-y: auto;
    padding: 16px 18px 18px; background: var(--nw-bg);
    border: 1px solid var(--nw-border); border-radius: 16px;
    box-shadow: 0 18px 50px -14px rgba(0, 0, 0, .55);
  }
  .nw-nchart-dlg-head { display: flex; align-items: center; justify-content: space-between; gap: 10px; margin-bottom: 12px; }
  .nw-nchart-dlg-title { font-size: 14px; font-weight: 600; color: var(--nw-text); }
  .nw-nchart-dlg-close {
    width: 30px; height: 30px; flex: none; border-radius: 9px;
    border: 1px solid var(--nw-border); background: none;
    color: var(--nw-text-secondary); font-size: 13px; line-height: 1; cursor: pointer;
  }
  .nw-nchart-dlg-close:hover { color: var(--nw-text); }
  @media (max-width: 640px) {
    .nw-nchart-dim { padding: 10px; }
    .nw-nchart-dialog { padding: 12px 12px 14px; border-radius: 14px; }
  }
  .nw-nchart-note { padding: 14px 12px; font-size: 12px; color: var(--nw-text-tertiary); }
  .nw-nchart-skel { margin: 8px; border-radius: 8px; background: var(--nw-bg-subtle); }
  .nw-nchart-more { padding: 0 12px 6px; font-size: 11px; color: var(--nw-text-tertiary); }
  .nw-nchart-rawwrap { padding: 0 12px 10px; font-size: 12px; }
  .nw-nchart-rawwrap summary { cursor: pointer; color: var(--nw-text-tertiary); }
  .nw-nchart-raw {
    background: rgba(0, 0, 0, 0.05);
    padding: 10px;
    border-radius: 8px;
    overflow-x: auto;
    font-size: 12px;
  }

  .nw-bubble blockquote {
    border-left: 3px solid var(--nw-border);
    padding-left: 12px;
    margin: 6px 0;
    color: var(--nw-text-secondary);
  }
  .nw-bubble h1, .nw-bubble h2, .nw-bubble h3, .nw-bubble h4 {
    font-weight: 600;
    margin: 8px 0 4px;
    line-height: 1.3;
  }
  .nw-bubble h1 { font-size: 17px; }
  .nw-bubble h2 { font-size: 15px; }
  .nw-bubble h3, .nw-bubble h4 { font-size: 14px; }
  .nw-bubble hr {
    border: none;
    border-top: 1px solid var(--nw-border);
    margin: 10px 0;
  }

  /* Images */
  .nw-bubble img {
    max-width: 100%;
    height: auto;
    border-radius: 8px;
    display: block;
    margin: 8px 0;
  }
  .nw-img-fallback {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    margin: 6px 0;
    background: var(--nw-bg-subtle);
    border: 1px dashed var(--nw-border);
    border-radius: 8px;
    color: var(--nw-text-secondary);
    font-size: 12.5px;
    font-style: italic;
  }
  .nw-img-fallback svg { width: 16px; height: 16px; flex-shrink: 0; }

  /* Mermaid diagrams (rendered by streamdown) */
  .nw-bubble svg[id^="mermaid"],
  .nw-bubble [class*="mermaid"] svg {
    max-width: 100%;
    height: auto;
    display: block;
    margin: 8px auto;
    background: var(--nw-bg);
    border-radius: 8px;
    padding: 8px;
  }
  .nw-bubble [class*="mermaid"] {
    display: block;
    margin: 8px 0;
    overflow-x: auto;
    text-align: center;
  }

  /* Thinking block (collapsible reasoning trace above an assistant bubble) */
  .nw-msg-stack {
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 0;
  }
  .nw-think {
    background: transparent;
    border: 1px solid var(--nw-border);
    border-radius: 12px;
    overflow: hidden;
    font-size: 12.5px;
    color: var(--nw-text-secondary);
  }
  .nw-think[open] {
    background: var(--nw-bg);
  }
  .nw-think-summary {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    cursor: pointer;
    user-select: none;
    list-style: none;
    color: var(--nw-text-secondary);
    transition: background 0.12s ease;
  }
  .nw-think-summary::-webkit-details-marker { display: none; }
  .nw-think-summary::marker { content: ""; }
  .nw-think-summary:hover { background: var(--nw-bg-subtle); }
  .nw-think[open] .nw-think-summary {
    border-bottom: 1px solid var(--nw-border);
  }
  .nw-think-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: var(--nw-text-tertiary);
    flex-shrink: 0;
  }
  .nw-think-dot.busy {
    background: var(--nw-primary);
    box-shadow: 0 0 0 0 rgba(99, 102, 241, 0.55);
    animation: nw-think-pulse 1.6s ease-out infinite;
  }
  @keyframes nw-think-pulse {
    0%   { box-shadow: 0 0 0 0 rgba(99, 102, 241, 0.6); }
    100% { box-shadow: 0 0 0 8px rgba(99, 102, 241, 0); }
  }
  /* "Still working" banner — sits above the answer while the turn is in
     flight so the user knows not to start reading; removed on completion. */
  .nw-status {
    display: flex;
    align-items: center;
    gap: 8px;
    align-self: flex-start;
    padding: 6px 11px;
    border-radius: 10px;
    background: var(--nw-bg-subtle);
    border: 1px solid var(--nw-border);
    color: var(--nw-text-secondary);
    font-size: 12.5px;
    font-weight: 600;
    letter-spacing: -0.005em;
    animation: nw-status-in 0.2s ease;
  }
  @keyframes nw-status-in {
    from { opacity: 0; transform: translateY(-2px); }
    to   { opacity: 1; transform: none; }
  }
  .nw-status-spinner {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    border: 2px solid var(--nw-border);
    border-top-color: var(--nw-primary);
    animation: nw-status-spin 0.7s linear infinite;
    flex-shrink: 0;
  }
  @keyframes nw-status-spin { to { transform: rotate(360deg); } }
  /* Each rotating word fades in as it swaps (re-keyed per word). */
  .nw-status-word {
    display: inline-block;
    animation: nw-status-word-in 0.35s ease;
  }
  @keyframes nw-status-word-in {
    from { opacity: 0; transform: translateY(1px); }
    to   { opacity: 1; transform: none; }
  }
  .nw-think-title {
    font-weight: 600;
    color: var(--nw-text);
    letter-spacing: -0.005em;
  }
  .nw-think-count {
    color: var(--nw-text-tertiary);
    font-variant-numeric: tabular-nums;
  }
  .nw-think-body {
    padding: 8px 12px 10px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    max-height: 240px;
    overflow-y: auto;
  }
  .nw-think-body::-webkit-scrollbar { width: 5px; }
  .nw-think-body::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.12); border-radius: 3px; }
  .nw-think-row {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    line-height: 1.45;
  }
  .nw-think-icon {
    flex-shrink: 0;
    width: 16px;
    text-align: center;
    color: var(--nw-text-tertiary);
    font-family: ui-monospace, "SF Mono", Menlo, monospace;
    font-size: 11.5px;
    margin-top: 1px;
  }
  .nw-think-label { color: var(--nw-text); }
  .nw-think-aside { color: var(--nw-text-tertiary); }
  .nw-think-code {
    background: rgba(0, 0, 0, 0.05);
    padding: 1px 5px;
    border-radius: 4px;
    font-family: ui-monospace, "SF Mono", Menlo, monospace;
    font-size: 11.5px;
    color: var(--nw-text);
  }
  .nw-think-ok .nw-think-icon { color: #16a34a; }
  .nw-think-err .nw-think-icon { color: #dc2626; }
  .nw-think-err .nw-think-aside { color: #b91c1c; }
  .nw-think-node {
    opacity: 0.7;
    font-size: 11.5px;
  }
  .nw-reasoning {
    white-space: pre-wrap;
    font-size: 12.5px;
    line-height: 1.55;
    color: var(--nw-text-secondary);
    background: var(--nw-bg-subtle);
    border-left: 2px solid var(--nw-primary);
    padding: 8px 12px;
    margin-bottom: 6px;
    border-radius: 0 6px 6px 0;
    font-style: italic;
    max-height: 200px;
    overflow-y: auto;
    word-wrap: break-word;
    overflow-wrap: anywhere;
  }
  .nw-reasoning-cursor {
    display: inline-block;
    color: var(--nw-primary);
    margin-left: 1px;
    animation: nw-blink 1s steps(2) infinite;
  }
  @keyframes nw-blink {
    0%, 50% { opacity: 1; }
    50.01%, 100% { opacity: 0; }
  }

  /* Clarify card (HITL multi-choice) */
  .nw-clarify {
    align-self: flex-start;
    max-width: 92%;
    background: var(--nw-bg);
    border: 1px solid var(--nw-border);
    border-radius: 14px;
    padding: 14px 14px 12px;
    box-shadow: 0 4px 10px -4px rgba(0, 0, 0, 0.06);
    display: flex;
    flex-direction: column;
    gap: 10px;
    animation: nw-clarify-in 0.18s cubic-bezier(0.16, 1, 0.3, 1);
  }
  @keyframes nw-clarify-in {
    from { opacity: 0; transform: translateY(4px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .nw-clarify.is-disabled {
    opacity: 0.55;
    filter: grayscale(0.2);
  }
  .nw-clarify-chip {
    align-self: flex-start;
    background: linear-gradient(135deg, rgba(99,102,241,0.12) 0%, rgba(139,92,246,0.12) 100%);
    color: var(--nw-primary-dark);
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.02em;
    padding: 3px 8px;
    border-radius: 6px;
    text-transform: uppercase;
  }
  .nw-clarify-text {
    font-size: 14px;
    color: var(--nw-text);
    line-height: 1.45;
  }
  .nw-clarify-options {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }
  .nw-clarify-option {
    background: var(--nw-bg-subtle);
    border: 1px solid var(--nw-border);
    border-radius: 10px;
    padding: 8px 14px;
    font-size: 13px;
    color: var(--nw-text);
    cursor: pointer;
    font-family: inherit;
    transition: all 0.15s ease;
  }
  .nw-clarify-option:hover:not(:disabled) {
    border-color: var(--nw-primary);
    background: rgba(99, 102, 241, 0.06);
    transform: translateY(-1px);
  }
  .nw-clarify-option:active:not(:disabled) { transform: translateY(0); }
  .nw-clarify-option:disabled { cursor: not-allowed; }
  .nw-clarify-text-row {
    display: flex;
    gap: 6px;
    margin-top: 2px;
  }
  .nw-clarify-input {
    flex: 1;
    border: 1px solid var(--nw-border);
    background: var(--nw-bg-subtle);
    border-radius: 10px;
    padding: 8px 12px;
    font-family: inherit;
    font-size: 13px;
    color: var(--nw-text);
    outline: none;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
  }
  .nw-clarify-input::placeholder { color: var(--nw-text-tertiary); }
  .nw-clarify-input:focus {
    border-color: var(--nw-primary);
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.12);
  }
  .nw-clarify-submit {
    background: linear-gradient(135deg, var(--nw-primary) 0%, var(--nw-primary-dark) 100%);
    color: #fff;
    border: none;
    border-radius: 10px;
    padding: 8px 14px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    font-family: inherit;
    transition: transform 0.15s ease, opacity 0.15s ease;
  }
  .nw-clarify-submit:hover:not(:disabled) { transform: scale(1.03); }
  .nw-clarify-submit:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    background: var(--nw-text-tertiary);
  }

  /* Download card — reuses the clarify card; the action is an anchor styled
     like the submit button. */
  .nw-download { gap: 8px; margin-top: 4px; }
  .nw-download-btn {
    align-self: flex-start;
    text-decoration: none;
    display: inline-block;
  }
  .nw-download-btn:hover { transform: scale(1.03); }

  /* Typing indicator */
  .nw-typing {
    display: flex;
    gap: 4px;
    padding: 14px 16px;
  }
  .nw-typing span {
    width: 7px;
    height: 7px;
    background: var(--nw-text-tertiary);
    border-radius: 50%;
    display: inline-block;
    animation: nw-bounce 1.3s ease-in-out infinite;
  }
  .nw-typing span:nth-child(2) { animation-delay: 0.18s; }
  .nw-typing span:nth-child(3) { animation-delay: 0.36s; }
  @keyframes nw-bounce {
    0%, 60%, 100% { transform: translateY(0); opacity: 0.5; }
    30% { transform: translateY(-6px); opacity: 1; }
  }

  /* Saved questions — the shared FaqPanel + FaqBar (faq.css). This wrapper only
     docks them above the composer and gives them the panel's gutters; the
     panel itself is display:none until open, so the gap collapses with it. */
  .nw-faq-dock {
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 0 12px 8px;
  }

  /* Input — the box itself is the shared chat-core Composer (composer.css);
     this wrapper just docks it to the panel bottom. */
  .nw-input-wrap {
    padding: 14px 16px 16px;
    background: var(--nw-bg);
    border-top: 1px solid var(--nw-border);
    flex-shrink: 0;
  }

  /* Outlook tab scroll + active-problems list. The canvas matches the
     outlook panel's --olx-bg so the padding around it blends in both themes. */
  .nw-outlook-scroll { padding: 14px; background: #eef3fc; }
  .nw-root.nw-theme-dark .nw-outlook-scroll { background: #0a1322; }

  /* Settings panel */
  .nw-settings {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 18px;
    display: flex;
    flex-direction: column;
    gap: 22px;
    background: var(--nw-bg);
  }
  .nw-settings-section {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  .nw-settings-label {
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--nw-text-secondary);
  }
  .nw-segment {
    display: flex;
    gap: 4px;
    background: var(--nw-bg-subtle);
    border: 1px solid var(--nw-border);
    border-radius: 12px;
    padding: 4px;
  }
  .nw-segment-btn {
    flex: 1;
    background: transparent;
    border: none;
    border-radius: 9px;
    padding: 8px 10px;
    font-family: inherit;
    font-size: 13px;
    font-weight: 500;
    color: var(--nw-text-secondary);
    cursor: pointer;
    transition: background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
  }
  .nw-segment-btn:hover { color: var(--nw-text); }
  .nw-segment-btn.is-active {
    background: var(--nw-bg);
    color: var(--nw-primary-dark);
    box-shadow: var(--nw-shadow-md);
  }
  .nw-settings-hint {
    font-size: 12px;
    color: var(--nw-text-tertiary);
    line-height: 1.45;
  }

  /* Toggle list card (e.g. SLA exclusions) */
  .nw-toggle-card {
    border: 1px solid var(--nw-border);
    border-radius: 12px;
    overflow: hidden;
    background: var(--nw-bg-subtle);
  }
  .nw-toggle-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    padding: 11px 14px;
  }
  .nw-toggle-item + .nw-toggle-item { border-top: 1px solid var(--nw-border); }
  .nw-toggle-info {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }
  .nw-toggle-title {
    font-size: 13.5px;
    font-weight: 500;
    color: var(--nw-text);
  }
  .nw-toggle-desc {
    font-size: 11.5px;
    color: var(--nw-text-tertiary);
    line-height: 1.4;
  }
  .nw-switch {
    flex-shrink: 0;
    width: 40px;
    height: 23px;
    border-radius: 999px;
    border: 1px solid var(--nw-border);
    background: var(--nw-bg-subtle);
    padding: 2px;
    cursor: pointer;
    transition: background 0.15s ease, border-color 0.15s ease;
    display: inline-flex;
    align-items: center;
  }
  .nw-switch:disabled { opacity: 0.5; cursor: wait; }
  .nw-switch.is-on {
    background: var(--nw-primary);
    border-color: var(--nw-primary);
  }
  .nw-switch-knob {
    width: 17px;
    height: 17px;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.25);
    transition: transform 0.15s ease;
  }
  .nw-switch.is-on .nw-switch-knob { transform: translateX(17px); }

  @media (max-width: 480px) {
    .nw-root,
    .nw-root.nw-pos-left { bottom: 16px; right: 16px; left: 16px; }
    .nw-panel,
    .nw-root.nw-pos-left .nw-panel {
      width: auto;
      left: 0;
      right: 0;
      height: calc(100vh - 100px);
    }
  }
`

/**
 * Read the logged-in Zabbix user from localStorage.
 *
 * Zabbix's notification subsystem stores per-user settings under a key
 * shaped like `<numeric-prefix>:notifications.user_settings`. The prefix
 * is session-bound and changes per user, so we scan for the suffix and
 * pull `payload.username` out of the value.
 *
 * Returns null on any failure — caller decides whether to fall back to
 * an attribute or fail. Wrapped in try/catch because the host page
 * might disable storage (private mode, strict cookie policies, etc.).
 *
 * If Zabbix ever changes this key format, this resolver returns null
 * and the widget falls back to the `username` attribute on the element.
 */
function resolveUsernameFromZabbixStorage(): string | null {
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (!key || !/^\d+:notifications\.user_settings$/.test(key)) continue
      const raw = localStorage.getItem(key)
      if (!raw) continue
      const data = JSON.parse(raw) as { payload?: { username?: unknown } } | null
      const username = data && data.payload && data.payload.username
      if (typeof username === 'string' && username.length > 0) {
        return username
      }
    }
  } catch (err) {
    console.warn('[Netwatch] Zabbix localStorage scan failed:', err)
  }
  return null
}

export class NetwatchWidgetElement extends HTMLElement {
  root: Root | null

  constructor() {
    super()
    this.attachShadow({ mode: 'open' })
    this.root = null
  }

  connectedCallback(): void {
    const apiEndpoint = this.getAttribute('api-endpoint') || 'http://localhost:8000'
    const clientId = this.getAttribute('client-id')
    // Username: explicit attribute wins (lets the host page override
    // when, e.g., running as a different effective user). Otherwise
    // auto-detect from Zabbix's localStorage — the common case when
    // embedded in the Zabbix UI.
    const username =
      this.getAttribute('username') || resolveUsernameFromZabbixStorage()

    if (!clientId) {
      console.error('NetwatchWidget: missing required `client-id` attribute')
      return
    }
    if (!username) {
      console.error(
        'NetwatchWidget: could not determine the logged-in user. ' +
        'Either pass `username` as an attribute or embed on a Zabbix page ' +
        'where the user is signed in.'
      )
      return
    }

    console.log(
      `%c[Netwatch] logged in as %c${username}%c on client %c${clientId}`,
      'color:#6b7280', 'color:#6366f1;font-weight:600',
      'color:#6b7280', 'color:#374151;font-family:monospace',
    )

    const shadow = this.shadowRoot as ShadowRoot

    const style = document.createElement('style')
    // ONE assignment, overrides last — a second assignment here once silently
    // dropped CHAT_CORE_OVERRIDES and let the outlook dialog scrim dim the
    // whole host page.
    style.textContent =
      WIDGET_CSS + '\n' + outlookCss + '\n' + emptyStateCss + '\n' + faqCss + '\n' + findingsCss + '\n'
      + composerCss + '\n' + salutationCss + '\n' + CHAT_CORE_OVERRIDES
    shadow.appendChild(style)

    const container = document.createElement('div')
    shadow.appendChild(container)

    this.root = ReactDOM.createRoot(container)
    this.root.render(
      <ChatWidget apiEndpoint={apiEndpoint} clientId={clientId} username={username} />
    )
  }

  disconnectedCallback(): void {
    if (this.root) {
      this.root.unmount()
      this.root = null
    }
  }
}

if (!customElements.get('netwatch-widget')) {
  customElements.define('netwatch-widget', NetwatchWidgetElement)
}
